/* eslint-disable complexity */
import dayjs from 'dayjs';
import cloneDeep from 'lodash/cloneDeep';

import { suspensionAnalytics } from '~/src/features/managementAgreement/SuspendServices/analytics';
import { dateFormat } from '~/src/features/managementAgreement/SuspendServices/helpers';
import { InferValueTypes } from '~/src/store';

import { getServicesBySuspendStatus } from './selectors/helpers/getServicesBySuspendStatus';
import { getServicesByType } from './selectors/helpers/getServicesByType';
import * as actionCreators from './actions';
import { MIN_SUSPEND } from './constants';
import { EServiceType, EStages, IState } from './types';

export type { IState } from './types';

export const actionTypes = {
    INITIALIZED: '@LK/SUSPEND/INITIALIZED',
    SET_HOW_IT_WORKS_DATA: '@LK/SUSPEND/SET_HOW_IT_WORKS_DATA',
    SET_LOADING: '@LK/SUSPEND/SET_LOADING',
    SET_ERROR: '@LK/SUSPEND/SET_ERROR',
    TOGGLE_CHECKED: '@LK/SUSPEND/TOGGLE_CHECKED',
    TOGGLE_SHOW_SUSPENDED: '@LK/SUSPEND/TOGGLE_SHOW_SUSPENDED',
    TOGGLE_SELECT_ALL: '@LK/SUSPEND/TOGGLE_SELECT_ALL',
    TOGGLE_SHOW_HOW_IT_WORKS: '@LK/SUSPEND/TOGGLE_SHOW_HOW_IT_WORKS',
    TOGGLE_CHANGE_DATES_FLOW: '@LK/SUSPEND/TOGGLE_CHANGE_DATES_FLOW',
    SET_SUSPEND_FROM: '@LK/SUSPEND/SET_SUSPEND_FROM',
    SET_SUSPEND_TO: '@LK/SUSPEND/SET_SUSPEND_TO',
    SET_STAGE: '@LK/SUSPEND/SET_STAGE',
    SET_PRICES: '@LK/SUSPEND/SET_PRICES',
    SET_RESULT_SUCCESS: '@LK/SUSPEND/SET_RESULT_SUCCESS',
    SET_CHECKED_ALL_INCLUDED: '@LK/SUSPEND/SET_CHECKED_ALL_INCLUDED',
    SET_DAYS_TO_UNSUSPEND_TV: '@LK/SUSPEND/SET_DAYS_TO_UNSUSPEND_TV',
    RESET: '@LK/SUSPEND/RESET',
} as const;

export type TAction = ReturnType<InferValueTypes<typeof actionCreators>>;

export const initState: IState = {
    isLoading: false,
    errorMessage: '',
    successResult: false,

    currentStage: EStages.init,
    showSuspended: false,
    showHowItWorks: false,
    changeDatesFlow: false,

    services: {},

    suspendFrom: '',
    suspendTo: '',

    suspendPrice: 0,
    recommendPay: 0,
    remainingPay: 0,
    balanceAfterSuspend: 0,
    daysToUnsuspendTv: 0,
};

export const reducer = (state: Readonly<IState> = initState, action: TAction): IState => {
    switch (action.type) {
        case actionTypes.INITIALIZED: {
            const services = action.payload.reduce<IState['services']>((acc, curr, index) => {
                acc[curr.id] = { ...curr, sortIdx: index };
                return acc;
            }, {});

            return {
                ...state,
                services,
                isLoading: false,
                errorMessage: '',
            };
        }
        case actionTypes.SET_HOW_IT_WORKS_DATA: {
            return {
                ...state,
                howItWorks: action.payload,
            };
        }
        case actionTypes.SET_LOADING: {
            return {
                ...state,
                isLoading: true,
            };
        }
        case actionTypes.SET_ERROR: {
            return {
                ...state,
                errorMessage: action.payload,
                isLoading: false,
            };
        }
        case actionTypes.TOGGLE_SHOW_HOW_IT_WORKS: {
            return {
                ...state,
                showHowItWorks: action.payload,
            };
        }
        case actionTypes.TOGGLE_CHANGE_DATES_FLOW: {
            return {
                ...state,
                changeDatesFlow: action.payload,
            };
        }
        case actionTypes.TOGGLE_CHECKED: {
            const newServices = cloneDeep(state.services);

            if (!newServices[action.payload].isChecked) {
                suspensionAnalytics.chooseService();
            }

            newServices[action.payload] = {
                ...newServices[action.payload],
                isChecked: !newServices[action.payload].isChecked,
            };

            return {
                ...state,
                services: newServices,
            };
        }
        case actionTypes.SET_CHECKED_ALL_INCLUDED: {
            const newServices = cloneDeep(state.services);
            const servicesBySuspendStatus = getServicesBySuspendStatus(Object.values(newServices), state.showSuspended);

            const allMainChecked = getServicesByType(servicesBySuspendStatus, EServiceType.main).every(el => el.isChecked);
            if (!allMainChecked) {
                return state;
            }

            const includedNotChecked = getServicesByType(servicesBySuspendStatus, EServiceType.included).filter(el => !el.isChecked);
            if (!includedNotChecked.length) {
                return state;
            }

            includedNotChecked.forEach(service => {
                service.isChecked = true;
            });
            return {
                ...state,
                services: newServices,
            };
        }
        case actionTypes.TOGGLE_SHOW_SUSPENDED: {
            return {
                ...state,
                showSuspended: !state.showSuspended,
            };
        }
        case actionTypes.TOGGLE_SELECT_ALL: {
            const isSelect = action.payload;
            const newServices = cloneDeep(state.services);
            const servicesBySuspendStatus = Object.values(newServices).filter(el => state.showSuspended ? el.isSuspended : !el.isSuspended);
            servicesBySuspendStatus.forEach(service => {
                service.isChecked = isSelect;
            });

            return {
                ...state,
                services: newServices,
            };
        }
        case actionTypes.SET_SUSPEND_FROM: {
            const suspendTo = state.suspendTo ? dayjs(state.suspendTo, dateFormat) : '';
            const diff = (suspendTo && action.payload) ? (suspendTo.diff(dayjs(action.payload, dateFormat), 'day')) : MIN_SUSPEND;

            return {
                ...state,
                suspendFrom: action.payload,
                suspendTo: diff < MIN_SUSPEND ? '' : state.suspendTo,
            };
        }
        case actionTypes.SET_SUSPEND_TO: {
            return {
                ...state,
                suspendTo: action.payload,
            };
        }
        case actionTypes.SET_STAGE: {
            return {
                ...state,
                currentStage: action.payload,
            };
        }
        case actionTypes.SET_PRICES: {
            return {
                ...state,
                ...action.payload,
                isLoading: false,
                errorMessage: '',
            };
        }
        case actionTypes.SET_RESULT_SUCCESS: {
            return {
                ...state,
                successResult: true,
                isLoading: false,
            };
        }
        case actionTypes.SET_DAYS_TO_UNSUSPEND_TV: {
            return {
                ...state,
                daysToUnsuspendTv: action.payload,
            };
        }
        case actionTypes.RESET: {
            return initState;
        }
        default: {
            const neverAction: never = action;
            return state || neverAction;
        }
    }
};
